import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { IValue } from '@platform500services/p500-ui-kit';
import { isEmpty } from '../../../../../helpers/is-empty';
import { IParsedDataResponse, IDataService } from '../../../../../services/data/data-service.interface';
import { getParsedValue } from '../../../../form-fields/pql-editor/pql-editor.helper';
import { AbstractConfig } from '../../../../../../modules/settings/trees/main-tree/widgets/trees/modal/tree-value/configs/abstract/abstract.config';

@Component({
  selector: 'gtd-ui-field-special-pql-type-free-new',
  templateUrl: './free.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPqlTypeFreeComponent implements OnInit, AfterViewInit {
  private globalData: IParsedDataResponse;

  constructor(@Inject('IDataService') private dataService: IDataService, private cd: ChangeDetectorRef) {
    this.globalData = this.dataService.getData();
  }

  @Input() public pql: string;
  public parsePql: any;
  @ViewChild('tmpl') public template: ElementRef<HTMLDivElement>;
  @HostBinding() public class = 'd-flex';

  public ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  public ngOnInit(): void {
    let skipIdx = -1;
    const parsePql = getParsedValue(this.pql).reduce((acc, item, i, arr) => {
      if (skipIdx === i) {
        return acc;
      }
      const nextItem = arr[i + 1];
      if (item.key === 'hourOfDay' && item.operator === '>=' && nextItem?.key === 'hourOfDay' && nextItem?.operator === '<') {
        acc.push({
          key: 'hourOfDay',
          config: null,
          operator: null,
          value: `${this.globalData.hoursFromData[item.value] || this.globalData.hoursToData[item.value] || item.value} — ${
            this.globalData.hoursFromData[nextItem.value] || this.globalData.hoursToData[nextItem.value] || nextItem.value
          }`,
        });
        skipIdx = i + 1;
        return acc;
      }
      if (item.key === 'hourOfDay') {
        acc.push(
          Object.assign({}, item, {
            value: this.globalData.hoursFromData[item.value] || this.globalData.hoursToData[item.value] || item.value,
          })
        );
        return acc;
      }
      acc.push(item);
      return acc;
    }, []);
    this.parsePql = parsePql.map((value) => this.getData(value));
  }

  public getData(value: IValue): {
    data: any[];
    cssClass: '-cyan' | '-violet' | '-orange' | '-yellow' | '-green' | '-gray';
    type: string;
    config: string;
    operator: string;
  } {
    const valueToArray = (value: any): any[] => (Array.isArray(value) ? value : [value]);
    const getValue = (value: string[] | string, data: Record<string, any>): any[] => valueToArray(value).map((item) => data[item] || item);
    const getBoolean = (value: any): string => (+value ? 'Yes' : 'No');
    const config = value.config;
    let operator = value.operator;
    switch (operator) {
      case '!=':
        operator = '≠';
        break;
    }
    switch (value.key) {
      case 'true':
        return {
          data: [],
          cssClass: '-cyan',
          type: 'Pass All',
          config,
          operator,
        };
      case 'affiliateUUID':
        return {
          data: getValue(value.value, this.globalData.affiliates),
          cssClass: '-yellow',
          type: 'Affiliate',
          config,
          operator,
        };
      case 'advertiserUUID':
        return {
          data: getValue(value.value, this.globalData.advertisers),
          cssClass: '-yellow',
          type: 'Advertiser',
          config,
          operator,
        };
      case 'offerUUID':
        return {
          data: getValue(value.value, this.globalData.offers),
          cssClass: '-cyan',
          type: 'Offer',
          config,
          operator,
        };
      case 'offerURLUUID':
        return {
          data: getValue(value.value, this.globalData.offer_urls),
          cssClass: '-green',
          type: 'URL',
          config,
          operator,
        };
      case 'userUUID':
        return {
          data: getValue(value.value, this.globalData.users),
          cssClass: '-cyan',
          type: 'User',
          config,
          operator,
        };
      case 'goalTypeUUID':
        return {
          data: getValue(value.value, this.globalData.goal_types),
          cssClass: '-yellow',
          type: 'Goal Type',
          config,
          operator,
        };
      case 'goalUUID':
        return {
          data: getValue(value.value, this.globalData.goals),
          cssClass: '-cyan',
          type: 'Goal',
          config,
          operator,
        };
      case 'baselineField':
        return {
          data: getValue(value.value, this.globalData.baseline_fields),
          cssClass: '-yellow',
          type: 'Baseline',
          config,
          operator,
        };
      case 'managerUUID':
        return {
          data: getValue(value.value, this.globalData.affiliate_managers),
          cssClass: '-green',
          type: 'Manager',
          config,
          operator,
        };
      case 'hourOfDay':
        return {
          data: value.value,
          cssClass: '-green',
          type: 'Hour',
          config,
          operator,
        };
      case 'dayOfWeek': {
        const [start] = value.value;
        const [end] = value.value.slice(-1);
        return {
          data: [`${start} — ${end}`],
          cssClass: '-green',
          type: 'Day',
          config,
          operator,
        };
      }
      case 'affiliateTransactionUUType': {
        return {
          data: getValue(value.value, this.globalData.transaction_types),
          cssClass: '-violet',
          type: 'Transaction Types',
          config,
          operator,
        };
      }
      case 'date': {
        return {
          data: value.value,
          cssClass: '-violet',
          type: 'Date',
          config,
          operator,
        };
      }
      default: {
        const type = AbstractConfig.getLabel(value.key.replace('affSub', 'Aff Sub '));
        if (typeof value.value === 'boolean') {
          value.value = getBoolean(value.value);
        }
        return {
          data: valueToArray(value.value).filter((v) => !isEmpty(v)),
          cssClass: '-violet',
          type,
          config,
          operator,
        };
      }
    }
  }
}
